/**
 * Daily Actions Hooks
 *
 * Provides React Query hooks for fetching and managing daily actions.
 *
 * @example
 * ```tsx
 * // Fetch daily actions
 * const { data, isLoading, refetch } = useDailyActions(organizationId);
 *
 * // Complete an action
 * const { mutate: complete } = useCompleteDailyAction();
 * complete({ actionId, leadId, activityType: 'call', notes: 'Spoke with contact' });
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { Timestamp } from 'firebase/firestore';
import { functions } from '../../config/firebase';
import type { DailyActionsResponse, CompleteActionRequest } from '../../types/dailyActions';

// ============================================================================
// TYPES
// ============================================================================

interface GenerateActionsRequest {
  organizationId: string;
  userId?: string;
  forceRefresh?: boolean;
  pipelineId?: string;
}

interface GenerateActionsResponse extends DailyActionsResponse {
  success: boolean;
  cached: boolean;
}

interface DismissActionRequest {
  actionId: string;
  reason?: string;
  followUpDate: string; // ISO date string — required
  notes?: string;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const dailyActionsKeys = {
  all: ['dailyActions'] as const,
  user: (userId: string, pipelineId?: string) =>
    pipelineId
      ? [...dailyActionsKeys.all, userId, pipelineId] as const
      : [...dailyActionsKeys.all, userId] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Fetch daily actions for the current user
 *
 * @param organizationId - Organization to fetch actions for
 * @param userId - Optional user override (defaults to current user)
 * @param pipelineId - Optional pipeline filter (undefined = all pipelines)
 *
 * @example
 * ```tsx
 * const { data, isLoading, isFetching, refetch } = useDailyActions(organizationId);
 *
 * // Access actions
 * data?.actions.map(action => ...)
 *
 * // Access pipeline health
 * data?.pipelineHealth.totalOpen
 *
 * // Force refresh
 * refetch();
 * ```
 */
export function useDailyActions(organizationId: string, userId?: string, pipelineId?: string) {
  return useQuery({
    queryKey: dailyActionsKeys.user(userId || 'current', pipelineId),
    queryFn: async (): Promise<DailyActionsResponse | null> => {
      if (!organizationId) return null;

      const fn = httpsCallable<GenerateActionsRequest, GenerateActionsResponse>(
        functions,
        'generateDailyActions'
      );

      const result = await fn({
        organizationId,
        userId,
        forceRefresh: false,
        pipelineId,
      });

      if (!result.data.success) {
        throw new Error('Failed to generate daily actions');
      }

      return {
        userId: result.data.userId,
        userName: result.data.userName,
        actions: result.data.actions,
        pipelineHealth: result.data.pipelineHealth,
        generatedAt: result.data.generatedAt,
        greeting: result.data.greeting,
      };
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Force refresh daily actions
 *
 * @example
 * ```tsx
 * const { mutate: refresh, isPending } = useRefreshDailyActions();
 * refresh({ organizationId });
 * ```
 */
export function useRefreshDailyActions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { organizationId: string; userId?: string; pipelineId?: string }): Promise<DailyActionsResponse> => {
      const fn = httpsCallable<GenerateActionsRequest, GenerateActionsResponse>(
        functions,
        'generateDailyActions'
      );

      const result = await fn({
        ...request,
        forceRefresh: true,
      });

      return {
        userId: result.data.userId,
        userName: result.data.userName,
        actions: result.data.actions,
        pipelineHealth: result.data.pipelineHealth,
        generatedAt: result.data.generatedAt,
        greeting: result.data.greeting,
      };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        dailyActionsKeys.user(variables.userId || 'current', variables.pipelineId),
        data
      );
    },
  });
}

/**
 * Complete a daily action
 *
 * @example
 * ```tsx
 * const { mutate: complete, isPending } = useCompleteDailyAction();
 *
 * complete({
 *   actionId: 'action_123',
 *   leadId: 'lead_456',
 *   activityType: 'call',
 *   notes: 'Left voicemail',
 * });
 * ```
 */
export function useCompleteDailyAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CompleteActionRequest): Promise<{ success: boolean }> => {
      const fn = httpsCallable<CompleteActionRequest, { success: boolean }>(
        functions,
        'completeDailyAction'
      );
      const result = await fn(request);
      return result.data;
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: dailyActionsKeys.all });
      const previousQueries = queryClient.getQueriesData<DailyActionsResponse>({
        queryKey: dailyActionsKeys.all,
      });
      for (const [queryKey, data] of previousQueries) {
        if (data) {
          queryClient.setQueryData<DailyActionsResponse>(queryKey, {
            ...data,
            actions: data.actions.map((a) =>
              a.actionId === request.actionId
                ? { ...a, status: 'completed' as const, completedAt: Timestamp.now() }
                : a
            ),
          });
        }
      }
      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Dismiss a daily action
 *
 * @example
 * ```tsx
 * const { mutate: dismiss } = useDismissDailyAction();
 * dismiss({ actionId: 'action_123', reason: 'Not relevant' });
 * ```
 */
export function useDismissDailyAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DismissActionRequest): Promise<{ success: boolean }> => {
      const fn = httpsCallable<DismissActionRequest, { success: boolean }>(
        functions,
        'dismissDailyAction'
      );
      const result = await fn(request);
      return result.data;
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: dailyActionsKeys.all });
      const previousQueries = queryClient.getQueriesData<DailyActionsResponse>({
        queryKey: dailyActionsKeys.all,
      });
      for (const [queryKey, data] of previousQueries) {
        if (data) {
          queryClient.setQueryData<DailyActionsResponse>(queryKey, {
            ...data,
            actions: data.actions.map((a) =>
              a.actionId === request.actionId
                ? { ...a, status: 'dismissed' as const, dismissedReason: request.reason }
                : a
            ),
          });
        }
      }
      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
    },
  });
}
